import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ActionsPopup, FullPageError } from 'src/components';
import DevTools from 'src/apps/Tools/DevTools';
import { ROUTES } from 'src/constants/routes.const';
import { useUserContext } from 'src/context/user';
import useSafePush from 'src/hooks/useSafePush';
import { PremiumConfigProps, usePremiumConfig } from 'src/hooks/usePremiumConfig';
import { useEligibility } from 'src/hooks/useEligibility';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { RestrictToAuth } from 'src/utilityComponents';
import { PremiumPrice } from 'src/api/internal/schema';
import { CommitmentUpsellSource } from 'src/api/tracking/types';
import { getBlobPlanPrices } from 'src/apps/Onboarding/BlobOnboarding/Plans/PlansComponent';
import { UpsellPageHeader } from '../UpsellPageHeader';
import { SubscribeHandlerConfig, useSubscribeHandler } from '../hooks/useSubscribeHandler';
import { usePageTemplate } from '../hooks/usePageTemplate';
import { useCondensedPlansPage } from '../hooks/useCondensedPlansPage';
import { getUpsellPageErrorInfo } from '../utils/getUpsellPageErrorInfo';
import { getPlansPageSource } from '../utils/getPlansPageSource';
import { BenefitsSection } from '../BenefitsSection';
import { FAQSection } from '../FAQSection';
import { PricesSection } from '../PricesSection';
import { PricesModal } from '../PricesModal';
import { ReviewSection } from '../ReviewSection';
import { FooterButtons } from '../FooterButtons';
import { CondensedUpsellPage } from '../CondensedPlansPage/CondensedUpsellPage';
import { CommitmentLedPlansPage } from '../CommitmentLedPlansPage';
import { BLOB_PLANS_TRACKING_LOCATION, BlobPlansPage } from '../BlobPlansPage';
import * as S from './styles';

export type UpsellPageLayout = 'commitmentLed' | 'commitmentPaywall';

export const msgs = defineMessages({
  alreadyPro: { id: 'web_plans_proalready_1' },
  errorCode: { id: 'web_chargebee_error_code' },
  backToHome: { id: 'ea_plans_back' },
  backToPlanPage: { id: 'learning_session_error_button_plans' },
});

export const UpsellPage: FC<{
  premiumConfigProps?: PremiumConfigProps;
  subscribeHandlerConfig?: SubscribeHandlerConfig;
  secondaryCta?: string;
  onClose?: () => void;
  onBack?: () => void;
  onLoadError?: () => void;
  layout?: UpsellPageLayout;
  /** Commitment layout only: where the upsell was shown, for its tracking events. */
  source?: CommitmentUpsellSource;
}> = ({
  premiumConfigProps,
  subscribeHandlerConfig,
  secondaryCta,
  onClose,
  onBack,
  onLoadError,
  layout,
  source,
}) => {
  const intl = useIntl();
  const { safePush } = useSafePush();
  const { user } = useUserContext();

  const [selectedPrice, setSelectedPrice] = useState<PremiumPrice | null>(null);
  const [countryOverride, setCountryOverride] = useState<string | undefined>(undefined);
  const [showPricesModal, setShowPricesModal] = useState(false);

  const secondaryAction = useMemo(
    () =>
      secondaryCta && onClose
        ? {
            text: secondaryCta,
            onClick: onClose,
          }
        : undefined,
    [secondaryCta, onClose],
  );

  const { error: premiumConfigError, premiumConfig } = usePremiumConfig({
    countryOverride,
    ...premiumConfigProps,
  });

  const isPaywallLayout = layout === 'commitmentPaywall';
  const { eligibility, isLoading: isEligibilityLoading } = useEligibility(
    isPaywallLayout ? getLanguagePairId() : null,
  );

  // Blob learners get the onboarding plans page, unless none of its plans are on offer.
  const isBlobPlansPage =
    isPaywallLayout &&
    !!eligibility?.eligibleForBlobExperience &&
    getBlobPlanPrices(premiumConfig?.prices).length > 0;

  const pageTemplate = usePageTemplate(premiumConfig);
  const isCondensedPlansPage = useCondensedPlansPage();
  const getTrackingSource = useCallback(() => source ?? getPlansPageSource(), [source]);
  const commitmentTrackingLocation = isBlobPlansPage
    ? BLOB_PLANS_TRACKING_LOCATION
    : ('commitment_upsell' as const);

  const {
    isWaitingForAuth,
    isCreatingSession,
    error: subscribeError,
    subscribeHandler,
  } = useSubscribeHandler(premiumConfig, selectedPrice, {
    ...subscribeHandlerConfig,
    ...(layout === 'commitmentLed' || layout === 'commitmentPaywall'
      ? { trackingLocation: commitmentTrackingLocation, getTrackingSource }
      : { getTrackingSource: getPlansPageSource }),
  });

  const { fullPageError, showAlreadyProModal } = getUpsellPageErrorInfo({
    user,
    premiumConfig,
    premiumConfigError,
    subscribeError,
  });

  useEffect(() => {
    if (premiumConfigError) {
      console.error(`Could not display upsell page: failed to load premium config.`);

      if (onLoadError) {
        onLoadError();
      }
    }
  }, [premiumConfigError, onLoadError]);

  if (fullPageError) {
    const { descriptionMsg, code } = fullPageError;
    return (
      <FullPageError
        buttonHref={ROUTES.payment.plans}
        buttonText={intl.formatMessage(msgs.backToPlanPage)}
        detail={intl.formatMessage(descriptionMsg)}
      >
        {code && (
          <div>
            {intl.formatMessage(msgs.errorCode)}: {code}
          </div>
        )}
      </FullPageError>
    );
  }

  if (!premiumConfig || !pageTemplate || isCreatingSession || isEligibilityLoading) {
    return <S.Loader />;
  }

  const Wrapper = isWaitingForAuth ? RestrictToAuth : React.Fragment;

  const sharedShell = (children: React.ReactNode) => (
    <Wrapper>
      <DevTools page="plans" setCountry={setCountryOverride} />

      <ActionsPopup
        header={intl.formatMessage(msgs.alreadyPro)}
        primaryBtnText={intl.formatMessage(msgs.backToHome)}
        primaryHref={ROUTES.dashboard}
        isOpen={showAlreadyProModal}
        onClose={() => safePush(ROUTES.dashboard)}
      />

      {children}
    </Wrapper>
  );

  if (isBlobPlansPage) {
    return sharedShell(
      <BlobPlansPage
        prices={premiumConfig.prices}
        onSubscribe={subscribeHandler}
        onClose={onClose}
        getSource={getTrackingSource}
      />,
    );
  }

  if (layout === 'commitmentLed' || layout === 'commitmentPaywall') {
    return sharedShell(
      <CommitmentLedPlansPage
        prices={premiumConfig.prices}
        selectedPrice={selectedPrice}
        setSelectedPrice={setSelectedPrice}
        onSubscribe={subscribeHandler}
        onClose={onClose}
        source={source}
        startOnPaywall={isPaywallLayout}
      />,
    );
  }

  if (isCondensedPlansPage) {
    return sharedShell(
      <CondensedUpsellPage
        prices={premiumConfig.prices}
        selectedPrice={selectedPrice}
        setSelectedPrice={setSelectedPrice}
        onSubscribe={subscribeHandler}
        onClose={onClose}
        onBack={onBack}
      />,
    );
  }

  return sharedShell(
    <>
      <PricesModal
        isOpen={showPricesModal}
        handleClose={() => setShowPricesModal(false)}
        prices={premiumConfig.prices}
        selectedPrice={selectedPrice}
        setSelectedPrice={setSelectedPrice}
        onSubscribe={subscribeHandler}
      />

      <UpsellPageHeader pageTemplate={pageTemplate} onClose={onClose} onBack={onBack} />

      <S.PageContent>
        <BenefitsSection pageTemplate={pageTemplate} />

        <PricesSection
          prices={premiumConfig.prices}
          selectedPrice={selectedPrice}
          setSelectedPrice={setSelectedPrice}
          onSubscribe={subscribeHandler}
          secondaryAction={secondaryAction}
        />

        <ReviewSection />

        <FAQSection />
      </S.PageContent>

      <S.StickyFooter>
        <FooterButtons
          onViewPrices={() => setShowPricesModal(true)}
          secondaryAction={secondaryAction}
        />
      </S.StickyFooter>
    </>,
  );
};
